import React, { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@foundation/components/ui/button';
import {
  AlertCircle,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { toCssFontFamily } from '@/features/revisr/utils/fontFamilyCss';

type BrandingProps = {
  logoUrl?: string;
  fontFamily?: string;
  primaryColor?: string;
  secondaryColor?: string;
  defaultColor?: string;
};

type GenerationStreamProps = {
  content: string;
  isStreaming: boolean;
  error: string | null;
  onRetry?: () => void;
  maxHeight?: string;
  className?: string;
  branding?: BrandingProps;
};

const GenerationStream: React.FC<GenerationStreamProps> = ({
  content,
  isStreaming,
  error,
  onRetry,
  maxHeight = '600px',
  className = '',
  branding,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScroll = useRef(true);

  // Scoped to policy content only — quoted so multi-word fonts (e.g. "Open Sans") resolve.
  const fontFamily = toCssFontFamily(branding?.fontFamily);
  // Policy fonts are forced to black — branding colours are intentionally ignored.
  const primaryColor = '#000000';
  const secondaryColor = '#000000';
  const defaultColor = '#000000';

  // Auto-scroll to bottom as content grows
  useEffect(() => {
    if (shouldAutoScroll.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [content]);

  // Detect manual scroll to disable auto-scroll
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    shouldAutoScroll.current = isNearBottom;
  };

  // Branded markdown components - consistent font sizes across all views
  const brandedComponents = {
    h1: ({ children }: { children: React.ReactNode }) => (
      <h1 style={{ color: primaryColor, fontFamily, fontSize: '2rem', fontWeight: 700, marginBottom: '1rem', marginTop: '1.5rem', lineHeight: 1.3, borderBottom: `2px solid ${primaryColor}20`, paddingBottom: '0.5rem' }}>{children}</h1>
    ),
    h2: ({ children }: { children: React.ReactNode }) => (
      <h2 style={{ color: secondaryColor, fontFamily, fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.85rem', marginTop: '2.5rem', lineHeight: 1.3, borderBottom: `2px solid ${secondaryColor}15`, paddingBottom: '0.4rem' }}>{children}</h2>
    ),
    // H3/H4 are subsections (e.g. "1.1", "1.2") — indent them under their parent
    // section so the document hierarchy reads visually, not just by numbering.
    h3: ({ children }: { children: React.ReactNode }) => (
      <h3 style={{ color: secondaryColor, fontFamily, fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem', marginTop: '1.5rem', lineHeight: 1.3, paddingLeft: '1.25rem' }}>{children}</h3>
    ),
    h4: ({ children }: { children: React.ReactNode }) => (
      <h4 style={{ color: secondaryColor, fontFamily, fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.4rem', marginTop: '1rem', paddingLeft: '2rem' }}>{children}</h4>
    ),
    // Body/lists sit under a subsection heading — indent to match the H3 offset.
    p: ({ children }: { children: React.ReactNode }) => (
      <p style={{ color: defaultColor, fontFamily, fontSize: '1rem', lineHeight: 1.75, marginBottom: '0.75rem', paddingLeft: '1.25rem' }}>{children}</p>
    ),
    ul: ({ children }: { children: React.ReactNode }) => (
      <ul style={{ color: defaultColor, fontFamily, fontSize: '1rem', lineHeight: 1.75, paddingLeft: '3rem', marginBottom: '0.75rem', listStyleType: 'disc' }}>{children}</ul>
    ),
    ol: ({ children }: { children: React.ReactNode }) => (
      <ol style={{ color: defaultColor, fontFamily, fontSize: '1rem', lineHeight: 1.75, paddingLeft: '3rem', marginBottom: '0.75rem', listStyleType: 'decimal' }}>{children}</ol>
    ),
    li: ({ children }: { children: React.ReactNode }) => (
      <li style={{ color: defaultColor, fontFamily, marginBottom: '0.35rem', fontSize: '1rem', paddingLeft: '0.25rem' }}>{children}</li>
    ),
    strong: ({ children }: { children: React.ReactNode }) => (
      <strong style={{ color: primaryColor, fontWeight: 600 }}>{children}</strong>
    ),
    table: ({ children }: { children: React.ReactNode }) => (
      <div style={{ overflowX: 'auto', marginBottom: '1.25rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem', fontFamily }}>{children}</table>
      </div>
    ),
    thead: ({ children }: { children: React.ReactNode }) => (
      <thead style={{ backgroundColor: `${primaryColor}08` }}>{children}</thead>
    ),
    th: ({ children }: { children: React.ReactNode }) => (
      <th style={{ backgroundColor: `${primaryColor}10`, color: primaryColor, fontWeight: 600, padding: '0.6rem 0.85rem', textAlign: 'left' as const, borderBottom: `2px solid ${primaryColor}30` }}>{children}</th>
    ),
    td: ({ children }: { children: React.ReactNode }) => (
      <td style={{ padding: '0.6rem 0.85rem', borderBottom: '1px solid #e5e7eb', color: defaultColor }}>{children}</td>
    ),
    blockquote: ({ children }: { children: React.ReactNode }) => (
      <blockquote style={{ borderLeft: `4px solid ${primaryColor}`, paddingLeft: '1.25rem', margin: '1rem 0', fontStyle: 'italic', color: '#6b7280', fontSize: '1rem' }}>{children}</blockquote>
    ),
    hr: () => (
      <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '1.5rem 0' }} />
    ),
    a: ({ href, children }: { href?: string; children: React.ReactNode }) => (
      <a href={href} style={{ color: primaryColor, textDecoration: 'underline' }} target={href?.startsWith('http') ? '_blank' : undefined} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}>{children}</a>
    ),
  };

  // Error state
  if (error && !content) {
    return (
      <div className={`flex flex-col items-center justify-center py-12 ${className}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-red-800 mb-1">Generation Failed</h3>
          <p className="text-sm text-red-600 mb-4">{error}</p>
          {onRetry && (
            <Button
              onClick={onRetry}
              variant="outline"
              size="sm"
              className="border-red-200 text-red-700 hover:bg-red-50"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Empty state (before streaming starts)
  if (!content && !isStreaming && !error) {
    return (
      <div className={`flex flex-col items-center justify-center py-12 text-gray-400 ${className}`}>
        <Loader2 className="h-8 w-8 mb-3 opacity-30" />
        <p className="text-sm">Waiting to generate...</p>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Scroll container */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="overflow-y-auto"
        style={{ maxHeight }}
      >
        <div className="p-6 generation-stream-content">
          {/* Branded logo header */}
          {branding?.logoUrl && (
            <div className="mb-4 pb-3 border-b" style={{ borderColor: `${primaryColor}15` }}>
              <img src={branding.logoUrl} alt="Organization" className="h-8 object-contain" />
            </div>
          )}

          {/* Rendered markdown content with branding */}
          {content && (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={brandedComponents as Record<string, React.ComponentType>}
            >
              {content}
            </ReactMarkdown>
          )}

          {/* Error banner (appears below content when partial content exists) */}
          {error && content && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-3">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-red-600">{error}</p>
              </div>
              {onRetry && (
                <Button
                  onClick={onRetry}
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0 border-red-200 text-red-700 hover:bg-red-50"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              )}
            </div>
          )}

          {/* Typing indicator */}
          {isStreaming && (
            <div className="flex items-center gap-1.5 mt-4 pb-2">
              <span className="flex gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-[#33b864] animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="h-1.5 w-1.5 rounded-full bg-[#33b864] animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="h-1.5 w-1.5 rounded-full bg-[#33b864] animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
              <span className="text-xs text-gray-400 ml-1">Generating...</span>
            </div>
          )}
        </div>
      </div>

      {/* Scroll-to-bottom indicator */}
      {isStreaming && !shouldAutoScroll.current && (
        <button
          onClick={() => {
            if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
              shouldAutoScroll.current = true;
            }
          }}
          className="absolute bottom-2 right-4 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs text-gray-500 shadow-sm hover:shadow-md transition-shadow flex items-center gap-1"
        >
          ↓ Scroll to bottom
        </button>
      )}
    </div>
  );
};

export default GenerationStream;
